// @stores mainStore/index.ts
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Config } from 'ziggy-js';
import { useEventBus, useLocalStorage } from '@vueuse/core';
import { useNotificationsStore } from '@notifications/stores';
import type {
  LivePropertiesCount,
  PortfolioConversion,
  SiteSettings,
} from '~/stores/mainStore/types';
import { globalKey } from '~/utils/events';
import { useAuth } from '#imports';

export const useMainStore = defineStore(
  'main',
  function () {
    useEventBus(globalKey);
    const notificationsStore = useNotificationsStore();
    const runtimeConfig = useRuntimeConfig();
    const apiBaseUrl = runtimeConfig.public.apiBaseUrl;
    const auth = useAuth();
    const page = ref<HTMLElement>();
    const ziggyConfig = useLocalStorage<Partial<Config>>('ZiggyConfig', {});
    const loadingAccountDetails = ref<boolean>(false);
    const boosts = ref<number>(0);
    const propertiesCount = ref<number>(0);
    const livePropertiesCount = ref<LivePropertiesCount>({
      live: 0,
      limit: 0,
      pending: false,
    });
    const spareRoomPropertiesCount = ref<LivePropertiesCount>({
      live: 0,
      limit: 0,
      pending: false,
    });
    const enquiriesCount = ref<number>(0);
    const liveUnreadEnquiriesCount = ref<number>(0);
    const totalAccounts = ref<number>(0);
    const portfolioConversion = ref<PortfolioConversion>({
      orderFormsCount: 0,
      totalForms: null,
      livePropertiesCount: 0,
      percentage: null,
      band: 'not_scored',
      season: {
        start: '',
        end: '',
      },
      previousPercentage: null,
      previousOrderFormsCount: null,
      previousLivePropertiesCount: null,
      pending: false,
      loaded: false,
    });

    const siteSettings = ref<SiteSettings>({
      advertTypes: {
        allowedTypes: [],
        options: {
          hmo: 'House in multiple occupation (HMO)',
          btr: 'Build to rent (BTR)',
        },
        showSelector: false,
      },
      allowDiscountCodeEntry: false,
      canViewCommissions: false,
      canViewUtilityConnect: false,
      canViewV2Tenancies: false,
      createTenancy: false,
      dataShouldBeObfuscated: false,
      defaultCommissionsSeason: '',
      disableSaveDraftTenancy: false,
      enableLettingAgentPortalExcelImport: true,
      enableUserHistory: false,
      enableUserManagement: false,
      handleDocuSignDeclinedForms: false,
      lowestBoostData: {
        quantity: 0,
        price: 0,
        pricePerBoost: 0,
      },
      minimumMonthsForBTRAvailability: 10,
      minimumMonthsForPropertyAvailability: 10,
      mobileFriendlyRoutes: [],
      rolePermissionsData: [],
      showAgentPortalAnalytics: false,
      showAgentPortalBetaToggleSwitch: false,
      showAgentPerformanceIndicator: false,
      utilityConnectPollingLength: 15,
      utilityConnectTemplateLink: '#',
      utilityConnectTermsAndConditionsLink: '#',
      utilityServiceOrderFormZipUrl: '#',
      allowEnquiryMailboxes: false,
      eSignBackdateMaxDate: null,
      eSignMinimumContractInterval: 'P10M',
      enableUtilityOrderPackagePricing: false,
      enablePropertyAdvertiseDateFields: false,
      spareRoomsEnabled: false,
      enableStripeCheckout: false,
      savedPaymentOptionsEnabled: false,
      fixedRateBoostEnabled: false,
      subscriptionsEnabled: false,
      twoFactorAuthBannerText: null,
    });

    // Getters
    const canUploadOrderForms = computed<boolean>(() => {
      if (!auth.user) {
        return false;
      }

      return auth.user.landlord.forms_upload_enabled;
    });

    const canCreateOrderForms = computed<boolean>(() => {
      if (!auth.user) {
        return false;
      }

      return auth.user.landlord.utilities_order_create_enabled;
    });

    const portfolioLink = computed<string>(() => {
      return `${apiBaseUrl}/student-accommodation?letting-agent=${auth.user.landlord.id}`;
    });

    // Actions
    function handleAccountLiveCountsUpdateEvent(
      event: Account.Events.LiveCountsUpdated,
    ) {
      livePropertiesCount.value = {
        live: event.livePropertiesCount,
        limit: event.livePropertiesLimit,
        pending: false,
      };

      spareRoomPropertiesCount.value = {
        live: event.spareRoomPropertiesCount,
        limit: event.spareRoomPropertiesLimit,
        pending: false,
      };

      liveUnreadEnquiriesCount.value = event.unreadEnquiriesCount;
      boosts.value = event.boostsCount;
    }

    function registerWebsocketEvents(userId: number): void {
      const { $echo } = useNuxtApp();
      // eslint-disable-next-line no-console
      console.log(
        '👂 Listening for Websocket Events on channel:',
        `private-agents-${userId}`,
      );

      $echo
        .private(`agents-${userId}`)
        .notification((notification: any) => {
          if (notification.type === 'notifications.enquiry.new') {
            notificationsStore.handleNewEnquiryNotification();
          }
        })
        .listen(
          '.account.liveCounts.update',
          (event: Account.Events.LiveCountsUpdated) =>
            handleAccountLiveCountsUpdateEvent(event),
        );
    }

    async function getAccountDetails(): Promise<void> {
      loadingAccountDetails.value = true;

      const { data } =
        await useApiFetch<Domain.Account.Profile.DataObjects.AccountDetailsData>(
          useZiggy('agents.account.all'),
        );

      boosts.value = data.value?.boostsCount ?? 0;
      enquiriesCount.value = data.value?.enquiriesCount ?? 0;
      liveUnreadEnquiriesCount.value = data.value?.unreadEnquiriesCount ?? 0;
      propertiesCount.value = data.value?.propertiesCount ?? 0;
      livePropertiesCount.value = {
        live: data.value?.livePropertiesCount ?? 0,
        limit: data.value?.livePropertiesLimit ?? 0,
      };
      spareRoomPropertiesCount.value = {
        live: data.value?.spareRoomPropertiesCount ?? 0,
        // Preserve null (unlimited); only default when the response is missing
        limit:
          data.value == null ? 0 : data.value.spareRoomPropertiesLimit,
      };
      totalAccounts.value = data.value?.totalAccounts ?? 0;
      loadingAccountDetails.value = false;
    }

    async function getPortfolioConversion(): Promise<void> {
      portfolioConversion.value.pending = true;

      try {
        const { data } =
          await useApiFetch<PortfolioConversion>(
            '/dashboard/portfolio-conversion',
          );

        if (data.value) {
          portfolioConversion.value = {
            ...data.value,
            pending: false,
            loaded: true,
          };
        } else {
          portfolioConversion.value.pending = false;
          portfolioConversion.value.loaded = true;
        }
      } catch {
        portfolioConversion.value.pending = false;
        portfolioConversion.value.loaded = true;
      }
    }

    async function getSiteSettings(): Promise<void> {
      const { data } = await useApiFetch<SiteSettings>('/site-settings');

      if (!data.value) return;

      siteSettings.value = data.value;

      if (data.value.showAgentPerformanceIndicator) {
        await getPortfolioConversion();
      }
    }

    function addBoosts(count: number): void {
      boosts.value += count;
    }

    function scrollToTop(offset: number = 0): void {
      if (page.value !== undefined && page.value !== null) {
        page.value.scrollTo(0, offset);
      }
    }

    interface ScrollOptions {
      element?: string | HTMLElement; // Element to scroll to
      offset?: number; // Optional offset from the element
      smooth?: boolean; // Whether to use smooth scrolling
    }

    function scrollToElement(options: ScrollOptions = {}): void {
      const { element, offset = 0, smooth = true } = options;

      // Get the target element and scrollable container
      let target: HTMLElement | null = null;
      let scrollContainer: HTMLElement | null = null;

      if (typeof element === 'string') {
        target = document.querySelector(element);
        // Find the closest scrollable parent
        if (target) {
          let parent = target.parentElement;
          while (parent) {
            const { overflow } = window.getComputedStyle(parent);
            if (overflow.includes('auto') || overflow.includes('scroll')) {
              scrollContainer = parent;
              break;
            }
            parent = parent.parentElement;
          }
        }
      } else if (element instanceof HTMLElement) {
        target = element;
      }

      if (target && scrollContainer) {
        // Get the position relative to the scroll container
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top;

        // Calculate the new scroll position
        const scrollPosition = relativeTop + scrollContainer.scrollTop - offset;

        // Scroll the container
        scrollContainer.scrollTo({
          top: scrollPosition,
          behavior: smooth ? 'smooth' : 'auto',
        });
      } else {
        // Fallback to original behavior
        // eslint-disable-next-line no-lonely-if
        if (page.value) {
          page.value.scrollTo({
            top: offset || 0,
            behavior: smooth ? 'smooth' : 'auto',
          });
        }
      }
    }

    return {
      auth,
      boosts,
      canCreateOrderForms,
      canUploadOrderForms,
      loadingAccountDetails,
      propertiesCount,
      livePropertiesCount,
      spareRoomPropertiesCount,
      enquiriesCount,
      liveUnreadEnquiriesCount,
      totalAccounts,
      portfolioConversion,
      page,
      portfolioLink,
      siteSettings,
      ziggyConfig,

      addBoosts,
      getAccountDetails,
      getPortfolioConversion,
      getSiteSettings,
      scrollToElement,
      scrollToTop,
      registerWebsocketEvents,
    };
  },
  {
    persist: [
      {
        paths: ['livePropertiesCount', 'spareRoomPropertiesCount', 'propertiesCount'],
        storage: localStorage,
      },
      {
        paths: [
          'boosts',
          'enquiriesCount',
          'canCreateOrderForms',
          'canUploadOrderForms',
          'liveUnreadEnquiriesCount',
          'portfolioLink',
          'totalAccounts',
          'siteSettings',
        ],
        storage: sessionStorage,
      },
    ],
  },
);
